import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Button, FormField, Input, Modal, ModalActions, ModalFootNote, Switch } from '../../components/shared';

/**
 * 「묶음 · 채널 만들기」 창 (2026-09-09 · 정본 F1 Modal · form).
 *
 * ★★★ **`window.prompt` 를 걷어낸 자리다.** 공지 슬라이드의 「추가」와 사내 방송의 「채널 추가」가
 *     브라우저 기본 프롬프트를 띄우고 있었다 — 테마도 글꼴도 안 따라가고, **이름 말고는 아무것도
 *     못 정하며**, 무엇을 만드는 것인지도 말해 주지 않았다.
 *
 * ★★★ **두 화면이 한 부품을 쓴다.** 묶음과 채널은 「이름 + 위젯에 걸까」 라는 **같은 모양**이라,
 *     따로 짜면 다음에 한쪽만 고쳐진다(이 판에서 «두 벌» 로 겪은 것과 같은 부류).
 *     낱말만 화면이 넘긴다 — 그 낱말은 **각 화면이 이미 쓰던 말 그대로**여야 한다
 *     (「위젯이 이 묶음을 돕니다」 · 「이 채널을 위젯에 건다」).
 *
 * ★★ **첫 개는 스위치를 잠근다.** 지금 코드가 「첫 묶음이면 자동으로 기본」 이라 그대로 두되,
 *     **왜 못 끄는지를 적는다** — 잠긴 채 이유가 없으면 고장으로 읽힌다.
 * ★★★ **Enter 로도 만들어진다. 대신 «한 번만».** 여기서 `disabled` 는 못 막는다 — 서버가 답하기
 *     전에 두 번째 Enter 가 들어오고, 그 사이 화면은 아직 안 바뀌었다. 그래서 `ref` 로 잠근다.
 *     ⚠ **잠금을 시간으로 풀면 안 된다**(`setTimeout` 으로 풀면 0.2초 뒤 두 번째가 그대로 들어간다).
 *     푸는 계기는 둘 — **오류가 왔거나**(다시 눌러야 한다) **이름을 고쳤을 때**(새로 만들 뜻이다).
 *
 * ⚠ 크기는 `sm` 이다. 정본 표는 form 을 md(720) 로 적지만, 이 창은 칸이 둘뿐이고
 *   **같은 통합설정 안의 형제 창**(「게시판 설정」)이 sm 이다 — 나란히 볼 화면과 맞춘다.
 */

export interface NewGroupModalProps {
  /** 창 제목 — 「슬라이드 묶음 만들기」 · 「채널 만들기」. */
  title: string;
  /** 이름 칸의 곁말과 예시. */
  namePlaceholder: string;
  /** 스위치 라벨 — **그 화면이 이미 쓰는 낱말** 그대로. */
  flagLabel: string;
  /** 스위치가 무엇을 뜻하는지 한 줄. */
  flagHint: string;
  /** 첫 개인가 — 그러면 스위치가 켜진 채 잠기고 이유를 적는다. */
  isFirst: boolean;
  /** 만든 뒤 어디서 나머지를 정하는지 — 창 아래 곁말. */
  footNote: string;
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (name: string, flag: boolean) => void;
}

const NAME_MAX = 40;

export function NewGroupModal({
  title, namePlaceholder, flagLabel, flagHint, isFirst, footNote,
  busy = false, error = null, onClose, onSubmit,
}: NewGroupModalProps) {
  const [name, setName] = useState('');
  const [flag, setFlag] = useState(isFirst);
  const [touched, setTouched] = useState(false);
  /** ★ 두 번 눌리지 않게 — 상태가 아니라 ref 다(다시 그려지기 전에 또 들어온다). */
  const sent = useRef(false);

  const trimmed = name.trim();
  const submit = () => {
    if (sent.current || busy) return;
    if (!trimmed) { setTouched(true); return; }
    sent.current = true;
    onSubmit(trimmed, isFirst ? true : flag);
  };

  /** 오류가 오면 다시 누를 수 있어야 한다 — 창은 그대로 서 있다. */
  useEffect(() => { if (error) sent.current = false; }, [error]);

  /** 한 줄 칸의 Enter — 폼이 없으므로 여기서 받는다(전송은 위에서 한 번만). */
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      size="sm"
      accent
      footer={(
        <>
          <ModalFootNote>{footNote}</ModalFootNote>
          <ModalActions>
            <Button variant="secondary" onClick={onClose} disabled={busy}>취소</Button>
            <Button variant="primary" onClick={submit} loading={busy} disabled={!trimmed}>만들기</Button>
          </ModalActions>
        </>
      )}
    >
      <FormField
        label="이름"
        required
        hint="목록과 배지에 이 이름이 보입니다."
        error={touched && !trimmed ? '이름을 적어 주세요' : undefined}
        counter={{ value: name.length, max: NAME_MAX }}
      >
        <Input
          value={name}
          onChange={(e) => { sent.current = false; setName(e.target.value); }}
          onKeyDown={onKeyDown}
          maxLength={NAME_MAX}
          placeholder={namePlaceholder}
          fullWidth
          autoFocus
        />
      </FormField>

      <FormField hint={isFirst ? '처음 만드는 것이라 자동으로 켜집니다 — 만든 뒤 바꿀 수 있습니다.' : flagHint}>
        <Switch
          checked={isFirst ? true : flag}
          onCheckedChange={setFlag}
          disabled={isFirst || busy}
          label={flagLabel}
        />
      </FormField>

      {error && (
        <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>
      )}
    </Modal>
  );
}
